import { z } from 'zod';
import { lenientString, lenientUuid, lenientEnum, lenientOptionalNode, lenientRequiredNode, lenientNodeArray } from '../schema-extensions';
import { SavingsType } from '../enums/savings-type.enum';
import { AccountIdentifierNode } from './account-identifier-node-schema';
import { ContactNode } from './contact-node-schema';
import { ValueEntryNode } from './value-entry-node-schema';
import { RateHistoryNode } from './rate-history-node-schema';

// ============================================================================
// CONSTANTS
// ============================================================================
const SAVINGS_CONSTANTS = {
  PROVIDER_MAX_LENGTH: 100,
  PLAN_MAX_LENGTH: 100,
  NAME_MAX_LENGTH: 100,
  NOTES_MAX_LENGTH: 1000,
  DATE_FORMAT_LENGTH: 10,
} as const;

// ============================================================================
// LENIENT SCHEMA (for deserialization)
// ============================================================================
const lenientSavingsNodeSchema = z.object({
  id: lenientUuid(),
  type: lenientEnum(SavingsType, SavingsType.SAVINGS_ACCOUNT),
  provider: lenientString(SAVINGS_CONSTANTS.PROVIDER_MAX_LENGTH),
  plan: lenientString(SAVINGS_CONSTANTS.PLAN_MAX_LENGTH),
  name: lenientString(SAVINGS_CONSTANTS.NAME_MAX_LENGTH),
  account: lenientOptionalNode(AccountIdentifierNode),
  owner: z.array(z.string()),
  value: lenientNodeArray(ValueEntryNode.deserialize),
  rates: lenientRequiredNode(RateHistoryNode),
  contact: lenientOptionalNode(ContactNode),
  notes: lenientString(SAVINGS_CONSTANTS.NOTES_MAX_LENGTH),
  closedDate: z.string().optional().catch(undefined).transform(val => val?.slice(0, 10)),
}).transform(val => ({
  id: val.id,
  type: val.type,
  provider: val.provider,
  plan: val.plan,
  name: val.name,
  account: val.account,
  owner: val.owner,
  value: val.value,
  rates: val.rates,
  contact: val.contact,
  notes: val.notes,
  closedDate: val.closedDate,
}));

// ============================================================================
// STRICT SCHEMA (for validation)
// ============================================================================
const strictSavingsNodeSchema = z.object({
  id: z.string().min(1, 'ID is required'),
  type: z.nativeEnum(SavingsType, { errorMap: () => ({ message: 'Invalid savings type' }) }),
  provider: z.string()
    .min(1, 'Provider is required')
    .max(SAVINGS_CONSTANTS.PROVIDER_MAX_LENGTH, `Provider must be ${SAVINGS_CONSTANTS.PROVIDER_MAX_LENGTH} characters or less`),
  plan: z.string()
    .min(1, 'Plan is required')
    .max(SAVINGS_CONSTANTS.PLAN_MAX_LENGTH, `Plan must be ${SAVINGS_CONSTANTS.PLAN_MAX_LENGTH} characters or less`),
  name: z.string()
    .min(1, 'Name is required')
    .max(SAVINGS_CONSTANTS.NAME_MAX_LENGTH, `Name must be ${SAVINGS_CONSTANTS.NAME_MAX_LENGTH} characters or less`),
  account: z.unknown().transform(val => AccountIdentifierNode.create(val)),
  owner: z.array(z.string()),
  value: z.array(z.unknown()).transform(arr => arr.map(val => ValueEntryNode.deserialize(val))),
  rates: z.unknown().transform(val => RateHistoryNode.deserialize(val)),
  contact: z.unknown().optional().transform(val => val ? ContactNode.create(val) : undefined),
  notes: z.string()
    .max(SAVINGS_CONSTANTS.NOTES_MAX_LENGTH, `Notes must be ${SAVINGS_CONSTANTS.NOTES_MAX_LENGTH} characters or less`)
    .optional()
    .transform(val => val ?? ''),
  closedDate: z.string().length(SAVINGS_CONSTANTS.DATE_FORMAT_LENGTH, 'Date must be in YYYY-MM-DD format').optional(),
}).transform(val => ({
  id: val.id,
  type: val.type,
  provider: val.provider,
  plan: val.plan,
  name: val.name,
  account: val.account,
  owner: val.owner,
  value: val.value,
  rates: val.rates,
  contact: val.contact,
  notes: val.notes,
  closedDate: val.closedDate,
}));

// ============================================================================
// TYPE
// ============================================================================
export type SavingsNode = z.infer<typeof lenientSavingsNodeSchema>;

// ============================================================================
// NAMESPACE (for Constants and Methods)
// ============================================================================
export namespace SavingsNode {
  export const Constants = SAVINGS_CONSTANTS;

  export function deserialize(data: unknown): SavingsNode {
    return lenientSavingsNodeSchema.parse(data);
  }

  export function create(data: unknown): SavingsNode {
    return strictSavingsNodeSchema.parse(data);
  }

  /**
   * Get the display name for a savings account
   */
  export function getDisplayName(node: SavingsNode): string {
    return node.name || `${node.provider} ${node.plan}`;
  }

  /**
   * Get the current value of the savings account
   */
  export function getCurrentValue(node: SavingsNode): number {
    if (node.value.length === 0) return 0;
    const sorted = [...node.value].sort((a, b) => b.date.localeCompare(a.date));
    return sorted[0].value;
  }

  /**
   * Get the current interest rate
   */
  export function getCurrentRate(node: SavingsNode): number {
    const currentRateNode = RateHistoryNode.getCurrentRate(node.rates);
    return currentRateNode.rate;
  }

  export function isActive(node: SavingsNode, referenceDate: Date = new Date()): boolean {
    return !node.closedDate || new Date(node.closedDate) > referenceDate;
  }
}
